// Single source for the sidebar, drawer and phone bottom bar (sprint 7 §6.2; sprint 11 §9.1).
import {
  CalendarClock,
  CalendarX,
  ChartNoAxesColumn,
  Clock,
  Folder,
  LayoutGrid,
  LayoutTemplate,
  Link2,
  NotebookPen,
  Settings,
  SlidersHorizontal,
  Table2,
  UserCheck,
  Users,
  type LucideIcon,
} from 'lucide-react';

export type NavGroupKey = 'track' | 'review' | 'oversight' | 'admin';

/**
 * Sprint 11: who sees an item. 'oversight' = a non-Admin Supervisor with viewTeam on a project.
 * Sprint 12: 'notes' = a non-Admin Supervisor holding a note capability on a project (me.access.notes).
 */
export type NavVisibility = 'all' | 'admin' | 'oversight' | 'notes' | 'shifts' | 'attendance' | 'myNotes';

export interface NavItem {
  key: string;
  label: string;
  /** Phone bottom-bar label. */
  shortLabel: string;
  path: string;
  icon: LucideIcon;
  visibility: NavVisibility;
  /** null = not in a group (Settings lives in the sidebar footer). */
  group: NavGroupKey | null;
  /** One of the 4 phone bottom tabs (the "+" sits between the 2nd and 3rd). */
  phoneTab: boolean;
}

// Order and glyphs follow the original sidebar: Dashboard, Time log, Projects | Reports.
export const NAV_ITEMS: readonly NavItem[] = [
  { key: 'dashboard', label: 'Dashboard', shortLabel: 'Home', path: '/', icon: LayoutGrid, visibility: 'all', group: 'track', phoneTab: true },
  { key: 'log', label: 'Time log', shortLabel: 'Log', path: '/log', icon: Clock, visibility: 'all', group: 'track', phoneTab: true },
  { key: 'projects', label: 'Projects', shortLabel: 'Projects', path: '/projects', icon: Folder, visibility: 'all', group: 'track', phoneTab: true },
  // Sprint 13: own shifts (me.access.shifts).
  { key: 'shifts', label: 'My shifts', shortLabel: 'Shifts', path: '/shifts', icon: CalendarClock, visibility: 'shifts', group: 'track', phoneTab: false },
  // Sprint 15 (§9.4): own self-notes (me.access.myNotes).
  { key: 'my-notes', label: 'My notes', shortLabel: 'Notes', path: '/notes', icon: NotebookPen, visibility: 'myNotes', group: 'track', phoneTab: false },
  { key: 'reports', label: 'Reports', shortLabel: 'Reports', path: '/reports', icon: ChartNoAxesColumn, visibility: 'all', group: 'review', phoneTab: true },
  { key: 'settings', label: 'Settings', shortLabel: 'Settings', path: '/settings', icon: Settings, visibility: 'all', group: null, phoneTab: false },
  { key: 'oversight-records', label: 'Records', shortLabel: 'Records', path: '/oversight/records', icon: Table2, visibility: 'oversight', group: 'oversight', phoneTab: false },
  { key: 'oversight-tracker', label: 'Missing entries', shortLabel: 'Missing', path: '/oversight/tracker', icon: CalendarX, visibility: 'oversight', group: 'oversight', phoneTab: false },
  { key: 'oversight-notes', label: 'Notes', shortLabel: 'Notes', path: '/oversight/notes', icon: NotebookPen, visibility: 'notes', group: 'oversight', phoneTab: false },
  // Sprint 13: non-Admin Supervisors (Admins use the project detail tabs).
  { key: 'oversight-attendance', label: 'Attendance', shortLabel: 'Attendance', path: '/oversight/attendance', icon: UserCheck, visibility: 'attendance', group: 'oversight', phoneTab: false },
  { key: 'users', label: 'Users', shortLabel: 'Users', path: '/admin/users', icon: Users, visibility: 'admin', group: 'admin', phoneTab: false },
  { key: 'records', label: 'Records', shortLabel: 'Records', path: '/admin/records', icon: Table2, visibility: 'admin', group: 'admin', phoneTab: false },
  { key: 'tracker', label: 'Missing entries', shortLabel: 'Missing', path: '/admin/tracker', icon: CalendarX, visibility: 'admin', group: 'admin', phoneTab: false },
  { key: 'links', label: 'Shared links', shortLabel: 'Links', path: '/admin/links', icon: Link2, visibility: 'admin', group: 'admin', phoneTab: false },
  { key: 'global-settings', label: 'Global settings', shortLabel: 'Global', path: '/admin/settings', icon: SlidersHorizontal, visibility: 'admin', group: 'admin', phoneTab: false },
  { key: 'templates', label: 'Templates', shortLabel: 'Templates', path: '/admin/templates', icon: LayoutTemplate, visibility: 'admin', group: 'admin', phoneTab: false },
];

export const NAV_GROUP_LABELS: Record<NavGroupKey, string> = { track: 'Track', review: 'Review', oversight: 'Oversight', admin: 'Admin' };

const GROUP_ORDER: readonly NavGroupKey[] = ['track', 'review', 'oversight', 'admin'];

export interface NavViewer {
  role: 'admin' | 'user';
  access: { oversight: boolean; notes?: boolean; shifts?: boolean; attendance?: boolean; myNotes?: boolean };
}

/** Items the viewer may see; hidden items are removed entirely (not disabled). Unknown viewer = a User. */
export function navItemsFor(me: NavViewer | undefined): NavItem[] {
  return NAV_ITEMS.filter((item) => {
    if (item.visibility === 'admin') return me?.role === 'admin';
    if (item.visibility === 'oversight') return me?.access.oversight === true;
    if (item.visibility === 'notes') return me?.access.notes === true;
    if (item.visibility === 'shifts') return me?.access.shifts === true;
    if (item.visibility === 'attendance') return me?.access.attendance === true;
    if (item.visibility === 'myNotes') return me?.access.myNotes === true;
    return true;
  });
}

/** Groups in order track, review, oversight, admin; empty groups are omitted; ungrouped items are skipped. */
export function groupNavItems(items: NavItem[]): Array<{ key: NavGroupKey; label: string; items: NavItem[] }> {
  return GROUP_ORDER.map((key) => ({ key, label: NAV_GROUP_LABELS[key], items: items.filter((i) => i.group === key) })).filter(
    (g) => g.items.length > 0,
  );
}

const SHARED_PREFIX = '/shared/';
const SHARED_TITLE = 'Shared report';

/**
 * Title for a path: the longest matching nav path wins; a shared link is "Shared report" (sprint 5).
 * No longer rendered since sprint 7 (pages carry their own PageHeader); kept exported and tested (A-6).
 */
export function titleForPath(pathname: string): string {
  if (pathname.startsWith(SHARED_PREFIX)) return SHARED_TITLE;
  const match = NAV_ITEMS.filter((i) => (i.path === '/' ? pathname === '/' : pathname === i.path || pathname.startsWith(`${i.path}/`))).sort(
    (a, b) => b.path.length - a.path.length,
  )[0];
  return match?.label ?? 'Clockit';
}
